import {
    CustomFormatType,
    getCustomFormat,
    getEffectiveItemType,
    getItemId,
    getItemMap,
    hasFormatting,
    isDimension,
    isMetric,
    NumberSeparator,
    type CustomFormat,
    type Dimension,
    type Metric,
} from '@lightdash/common';
import { Button } from '@mantine-8/core';
import { useForm } from '@mantine/form';
import { IconEraser, IconPencil } from '@tabler/icons-react';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { type ValueOf } from 'type-fest';
import {
    explorerActions,
    selectAdditionalMetrics,
    selectFormatModal,
    selectMetricQuery,
    selectTableCalculations,
    selectTableName,
    useExplorerDispatch,
    useExplorerSelector,
} from '../../../features/explorer/store';
import { useExplore } from '../../../hooks/useExplore';
import MantineIcon from '../../common/MantineIcon';
import MantineModal from '../../common/MantineModal';
import { FormatForm } from '../FormatForm';

const DEFAULT_FORMAT: CustomFormat = {
    type: CustomFormatType.DEFAULT,
    round: undefined,
    separator: NumberSeparator.DEFAULT,
    currency: undefined,
    compact: undefined,
    prefix: undefined,
    suffix: undefined,
};

// Returns a fresh copy to avoid the module-level constant being frozen by Immer
// when passed through Redux. klona (used by Mantine form) preserves frozen
// property descriptors, causing "Cannot assign to read only property" errors.
const getDefaultFormat = (): CustomFormat => ({ ...DEFAULT_FORMAT });

export const FormatModal = memo(() => {
    const dispatch = useExplorerDispatch();
    const { isOpen, item } = useExplorerSelector(selectFormatModal);
    const metricQuery = useExplorerSelector(selectMetricQuery);
    const metricOverrides = metricQuery.metricOverrides;
    const dimensionOverrides = metricQuery.dimensionOverrides;
    const additionalMetrics = useExplorerSelector(selectAdditionalMetrics);
    const tableCalculations = useExplorerSelector(selectTableCalculations);
    const tableName = useExplorerSelector(selectTableName);

    // Get explore data to determine effective item type for formatting
    const { data: exploreData } = useExplore(tableName, {
        refetchOnMount: false,
    });

    const itemsMap = useMemo(() => {
        if (exploreData) {
            return getItemMap(
                exploreData,
                additionalMetrics,
                tableCalculations,
            );
        }
        return undefined;
    }, [exploreData, additionalMetrics, tableCalculations]);

    const toggleModal = useCallback(() => {
        dispatch(explorerActions.toggleFormatModal());
    }, [dispatch]);

    const updateMetricFormat = useCallback(
        (payload: { metric: Metric; formatOptions: CustomFormat }) => {
            dispatch(explorerActions.updateMetricFormat(payload));
        },
        [dispatch],
    );

    const updateDimensionFormat = useCallback(
        (payload: { dimension: Dimension; formatOptions: CustomFormat }) => {
            dispatch(explorerActions.updateDimensionFormat(payload));
        },
        [dispatch],
    );

    const form = useForm<{ format: CustomFormat }>({
        validateInputOnChange: true,
        initialValues: {
            format: getDefaultFormat(),
        },
    });

    const { setFieldValue } = form;

    useEffect(() => {
        if (item) {
            const itemFormat = hasFormatting(item)
                ? getCustomFormat(item)
                : undefined;

            // Get the appropriate override based on item type
            const override = isDimension(item)
                ? dimensionOverrides?.[getItemId(item)]
                : metricOverrides?.[getItemId(item)];

            if (itemFormat || override) {
                setFieldValue('format', {
                    ...DEFAULT_FORMAT,
                    ...itemFormat,
                    ...override?.formatOptions,
                });
            }
        }
    }, [metricOverrides, dimensionOverrides, item, setFieldValue]);

    const handleClose = useCallback(() => {
        form.reset();
        toggleModal();
    }, [form, toggleModal]);

    const handleOnSubmit = form.onSubmit((values) => {
        if (!item) return;
        if (isMetric(item)) {
            updateMetricFormat({ metric: item, formatOptions: values.format });
        } else if (isDimension(item)) {
            updateDimensionFormat({
                dimension: item,
                formatOptions: values.format,
            });
        }
        dispatch(explorerActions.requestQueryExecution());
        handleClose();
    });

    const getFormatInputProps = (path: keyof CustomFormat) =>
        form.getInputProps(`format.${path}`);

    const setFormatFieldValue = (
        path: keyof CustomFormat,
        value: ValueOf<CustomFormat>,
    ) => form.setFieldValue(`format.${path}`, value);

    const modalTitle = useMemo(() => {
        if (isDimension(item)) {
            return 'Format dimension';
        }
        if (isMetric(item)) {
            return 'Format metric';
        }
        return 'Format field';
    }, [item]);

    if (!isOpen || !item) {
        return null;
    }

    return (
        <MantineModal
            size="xl"
            opened
            icon={IconPencil}
            onClose={handleClose}
            title={modalTitle}
            actions={
                <Button type="submit" form="format-field-form">
                    Save changes
                </Button>
            }
            leftActions={
                !isEqual(form.values.format, DEFAULT_FORMAT) ? (
                    <Button
                        variant="default"
                        leftSection={<MantineIcon icon={IconEraser} />}
                        onClick={() =>
                            form.setValues({
                                format: getDefaultFormat(),
                            })
                        }
                    >
                        Reset
                    </Button>
                ) : undefined
            }
        >
            <form id="format-field-form" onSubmit={handleOnSubmit}>
                <FormatForm
                    formatInputProps={getFormatInputProps}
                    format={form.values.format}
                    setFormatFieldValue={setFormatFieldValue}
                    itemType={getEffectiveItemType(item, itemsMap)}
                />
            </form>
        </MantineModal>
    );
});
